import { getFreeContactsVisibility } from "../../Utils/freeRsvpVisibility";
import { useEffect, useMemo, useState } from "react";
import {
  deleteSeatingTableTransactional,
  getDocument,
  saveSeatingTableTransactional,
  updateContactTransactional,
} from "../../firebase";
import { exportSeatingToSpreadsheet } from "../../Utils";
import "./SeatingArrangements.css";

const getPartySize = (contact) => {
  const parsed = parseInt(String(contact?.howMany || "1"), 10);
  return Number.isNaN(parsed) || parsed < 1 ? 1 : parsed;
};

const createTableId = () => `table-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const normalizeTableNumber = (value) => String(value || "").trim();

const sortTables = (tables) =>
  [...tables].sort((a, b) => {
    const aNum = parseInt(String(a.number), 10);
    const bNum = parseInt(String(b.number), 10);
    if (!Number.isNaN(aNum) && !Number.isNaN(bNum) && aNum !== bNum) return aNum - bNum;
    return String(a.number || "").localeCompare(String(b.number || ""), "he");
  });

const escapeHtml = (value) =>
  String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const getSeatPosition = (seatIndex, seatCount) => {
  const angle = -Math.PI / 2 + (2 * Math.PI * seatIndex) / Math.max(seatCount, 1);
  return {
    left: `${50 + 37 * Math.cos(angle)}%`,
    top: `${50 + 35 * Math.sin(angle)}%`,
  };
};

const SeatingArrangements = ({ addAlertText, docID, isReadOnly = false }) => {
  const [contacts, setContacts] = useState([]);
  const [eventAccess, setEventAccess] = useState({});
  const visibleContacts = getFreeContactsVisibility(eventAccess, contacts).visibleContacts;
  const [tables, setTables] = useState([]);
  const [newTable, setNewTable] = useState({ name: "", number: "", capacity: "10" });
  const [tableEdits, setTableEdits] = useState({});
  const [editingTableId, setEditingTableId] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [removeConfirmIdx, setRemoveConfirmIdx] = useState(null);
  const [expandedGuestTables, setExpandedGuestTables] = useState({});
  const [showAddTableForm, setShowAddTableForm] = useState(false);
  const [showFirstTableWarning, setShowFirstTableWarning] = useState(false);
  const sortedTables = useMemo(() => sortTables(tables), [tables]);

  useEffect(() => {
    if (!docID) return;
    getDocument({ docID }).then((res) => {
      const data = res.data();
      setEventAccess(data);
      const loadedTables = Array.isArray(data.seatingTables) ? data.seatingTables : [];
      const firstTable = sortTables(loadedTables)[0];
      setContacts(Array.isArray(data.contacts) ? data.contacts : []);
      setTables(loadedTables);
      setExpandedGuestTables(firstTable ? { [firstTable.id]: true } : {});
    });
  }, [docID]);

  const assignedTableIds = useMemo(() => new Set(tables.map((table) => table.id)), [tables]);

  const getAssignedContacts = (tableId) =>
    contacts
      .map((contact, idx) => ({ contact, idx }))
      .filter(({ contact }) => visibleContacts.includes(contact) && contact.tableNum === tableId);

  const getOccupiedSeats = (tableId, list = visibleContacts) =>
    list.reduce((sum, contact) => (contact.tableNum === tableId ? sum + getPartySize(contact) : sum), 0);

  const toggleGuestTable = (tableId) => {
    setExpandedGuestTables((prev) => ({ ...prev, [tableId]: !prev[tableId] }));
  };

  const isDuplicateTableNumber = (number, tableId = "") => {
    const normalized = normalizeTableNumber(number);
    return tables.some((table) => table.id !== tableId && normalizeTableNumber(table.number) === normalized);
  };

  const handleAddTable = async (skipFirstTableWarning = false) => {
    if (isReadOnly) return;
    const name = newTable.name.trim();
    const number = normalizeTableNumber(newTable.number);
    const capacity = parseInt(String(newTable.capacity), 10);
    if (!name) {
      addAlertText({ mode: "warning", text: "יש להזין שם שולחן." });
      return;
    }
    if (!number) {
      addAlertText({ mode: "warning", text: "יש להזין מספר שולחן." });
      return;
    }
    if (isDuplicateTableNumber(number)) {
      addAlertText({ mode: "warning", text: "מספר שולחן זה כבר קיים." });
      return;
    }
    if (Number.isNaN(capacity) || capacity < 1) {
      addAlertText({ mode: "warning", text: "יש להזין מספר מקומות תקין." });
      return;
    }
    if (tables.length === 0 && !skipFirstTableWarning) {
      setShowFirstTableWarning(true);
      return;
    }

    const table = { id: createTableId(), name, number, capacity };
    try {
      const saved = await saveSeatingTableTransactional({ docID, table, allowCreate: true });
      setTables(saved.tables);
    } catch (error) {
      addAlertText({
        mode: error?.code === "SEATING_TABLE_NUMBER_EXISTS" ? "warning" : "danger",
        text: error?.code === "SEATING_TABLE_NUMBER_EXISTS"
          ? "מספר שולחן זה כבר קיים."
          : "לא הצלחנו להוסיף את השולחן. נסו שוב.",
      });
      return;
    }
    setNewTable({ name: "", number: "", capacity: "10" });
    setShowAddTableForm(false);
    addAlertText({ mode: "success", text: "השולחן נוסף בהצלחה." });
  };

  const getDraft = (table) => tableEdits[table.id] || table;

  const handleTableEdit = (table, field, value) => {
    setTableEdits((prev) => ({
      ...prev,
      [table.id]: { ...getDraft(table), [field]: value },
    }));
  };

  const handleSaveTable = async (table) => {
    if (isReadOnly) return;
    const draft = getDraft(table);
    const name = String(draft.name || "").trim();
    const number = normalizeTableNumber(draft.number);
    const capacity = parseInt(String(draft.capacity), 10);
    const occupiedSeats = getOccupiedSeats(table.id, contacts);

    if (!name || !number) {
      addAlertText({ mode: "warning", text: "יש למלא שם ומספר שולחן." });
      return;
    }
    if (isDuplicateTableNumber(number, table.id)) {
      addAlertText({ mode: "warning", text: "מספר שולחן זה כבר קיים." });
      return;
    }
    if (Number.isNaN(capacity) || capacity < 1) {
      addAlertText({ mode: "warning", text: "יש להזין מספר מקומות תקין." });
      return;
    }
    if (capacity < occupiedSeats) {
      addAlertText({
        mode: "warning",
        text: eventAccess.rsvpAccess?.classification === "FREE" ? "אי אפשר להקטין את השולחן מתחת למספר המקומות התפוסים." : `אי אפשר להקטין ל-${capacity} מקומות כי כבר תפוסים ${occupiedSeats} מקומות. יש להסיר קודם אורחים מהשולחן כדי לצמצם את המקומות בשולחן.`,
      });
      return;
    }

    const updatedTable = { ...table, name, number, capacity };
    try {
      const saved = await saveSeatingTableTransactional({ docID, table: updatedTable });
      setTables(saved.tables);
    } catch (error) {
      addAlertText({
        mode: "warning",
        text: error?.code === "SEATING_TABLE_CAPACITY_EXCEEDED"
          ? eventAccess.rsvpAccess?.classification === "FREE" ? "אי אפשר להקטין את השולחן מתחת למספר המקומות התפוסים." : `אי אפשר להקטין את השולחן כי כבר תפוסים ${error.occupiedSeats} מקומות.`
          : error?.code === "SEATING_TABLE_NUMBER_EXISTS"
            ? "מספר שולחן זה כבר קיים."
            : "לא הצלחנו לשמור את השולחן.",
      });
      return;
    }
    setTableEdits((prev) => {
      const next = { ...prev };
      delete next[table.id];
      return next;
    });
    setEditingTableId(null);
    setDeleteConfirmId(null);
    addAlertText({ mode: "success", text: "השולחן נשמר." });
  };

  const handleCancelTableEdit = (tableId) => {
    setTableEdits((prev) => {
      const next = { ...prev };
      delete next[tableId];
      return next;
    });
    setEditingTableId(null);
    setDeleteConfirmId(null);
  };

  const handleCapacityChange = async (table, delta) => {
    if (isReadOnly) return;
    const nextCapacity = (parseInt(String(table.capacity), 10) || 1) + delta;
    const occupiedSeats = getOccupiedSeats(table.id, contacts);
    if (nextCapacity < 1) return;
    if (nextCapacity < occupiedSeats) {
      addAlertText({
        mode: "warning",
        text: eventAccess.rsvpAccess?.classification === "FREE" ? "אי אפשר להסיר מקומות תפוסים." : `אי אפשר להסיר מקומות. קודם יש להסיר אורחים מהשולחן. תפוסים ${occupiedSeats} מקומות.`,
      });
      return;
    }
    try {
      const saved = await saveSeatingTableTransactional({
        docID,
        table: { ...table, capacity: nextCapacity },
      });
      setTables(saved.tables);
    } catch (error) {
      addAlertText({ mode: "warning", text: "לא הצלחנו לעדכן את מספר המקומות בשולחן." });
    }
  };

  const handleDeleteTable = async (tableId) => {
    if (isReadOnly) return;
    try {
      const saved = await deleteSeatingTableTransactional({ docID, tableId });
      setDeleteConfirmId(null);
      setTables(saved.tables);
      setContacts(saved.contacts);
    } catch (error) {
      addAlertText({ mode: "danger", text: "לא הצלחנו למחוק את השולחן. לא בוצע שינוי." });
      return;
    }
    addAlertText({ mode: "success", text: "השולחן נמחק והאורחים הוסרו ממנו." });
  };

  const handleRemoveParty = async (contactIdx) => {
    if (isReadOnly) return;
    const contact = contacts[contactIdx];
    if (!contact) return;
    try {
      const saved = await updateContactTransactional({
        docID,
        phoneNumber: contact.phoneNumber,
        patch: { tableNum: "" },
      });
      setContacts(saved.contacts);
    } catch (error) {
      addAlertText({ mode: "danger", text: "לא הצלחנו להסיר את האורח מהשולחן." });
      return;
    }
    setRemoveConfirmIdx(null);
    addAlertText({ mode: "success", text: `${contact.name} הוסר/ה מהשולחן.`, preventScroll: true });
  };

  const totalSeats = tables.reduce((sum, table) => sum + (parseInt(String(table.capacity), 10) || 0), 0);
  const occupiedSeats = visibleContacts.reduce(
    (sum, contact) => (assignedTableIds.has(contact.tableNum) ? sum + getPartySize(contact) : sum),
    0
  );
  const unassignedApprovedPeople = visibleContacts.reduce(
    (sum, contact) => (contact.status === "approved" && !assignedTableIds.has(contact.tableNum) ? sum + getPartySize(contact) : sum),
    0
  );
  const handlePdfExport = () => {
    const tableSections = sortedTables
      .map((table) => {
        const assigned = getAssignedContacts(table.id);
        const seats = assigned
          .map(({ contact }) => `<li>${escapeHtml(contact.name)} (${getPartySize(contact)} מקומות)</li>`)
          .join("");
        return `
          <section>
            <h2>${escapeHtml(table.name)} - שולחן ${escapeHtml(table.number)}</h2>
            <p>מקומות תפוסים: ${getOccupiedSeats(table.id)} | מקומות פנויים: ${
          (parseInt(String(table.capacity), 10) || 0) - getOccupiedSeats(table.id)
        } | סך הכל: ${escapeHtml(table.capacity)}</p>
            <ol>${seats || "<li>אין אורחים בשולחן זה</li>"}</ol>
          </section>
        `;
      })
      .join("");
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      addAlertText({ mode: "warning", text: "הדפדפן חסם את פתיחת חלון ה-PDF." });
      return;
    }
    printWindow.document.write(`
      <html>
        <head>
          <title>סידורי הושבה</title>
          <style>
            body { direction: rtl; font-family: Arial, sans-serif; padding: 24px; color: #1f2933; }
            h1 { text-align: center; }
            section { border: 1px solid #d8dee9; border-radius: 10px; padding: 14px; margin-bottom: 16px; page-break-inside: avoid; }
            h2 { margin: 0 0 8px; }
            li { margin-bottom: 6px; }
          </style>
        </head>
        <body>
          <h1>סידורי הושבה</h1>
          <p>מקומות תפוסים: ${occupiedSeats} | מקומות פנויים: ${Math.max(totalSeats - occupiedSeats, 0)}</p>
          ${tableSections}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  return (
    <div className="seating-page col-12" dir="rtl">
      <div className="seating-page__inner">
        {showFirstTableWarning && (
          <div className="seating-modal">
            <div className="seating-modal__content">
              <h3>מערכת סידורי הושבה</h3>
              <p>
                יש להשתמש בסידורי הושבה רק בסמוך לאירוע, כשרשימת האורחים כבר סגורה. לאחר שהוקצה לאורח מקום, הוא לא יוכל לעדכן את כמות האנשים שבאים איתו, ויהיה צריך לעשות זאת ידנית בכדי שלא תהיה בעיה עם כמות אנשים שלא מתאימה לשולחן או חורים.
              </p>
              <p>לכן, חשוב לוודא שרשימת האורחים סופית עד כמה שניתן.</p>
              <div className="seating-modal__actions">
                <button className="btn btn-success btn-sm" disabled={isReadOnly} onClick={() => { setShowFirstTableWarning(false); handleAddTable(true); }}>
                  הבנתי, צור שולחן
                </button>
                <button className="btn btn-outline-secondary btn-sm" onClick={() => setShowFirstTableWarning(false)}>
                  ביטול
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="seating-hero">
          <div>
            <h2 className="seating-hero__title">סידורי הושבה</h2>
            <p className="seating-hero__text">ניהול שולחנות, מקומות ואורחים לפי רשימת אנשי הקשר.</p>
            {getFreeContactsVisibility(eventAccess, contacts).isLimited && <p role="status">הסיכומים והייצוא כוללים את 50 אנשי הקשר המוצגים בלבד. כל השיבוצים נשמרים.</p>}
          </div>
          <div className="seating-summary">
            <div className="seating-summary__seat-counts">
              <span>מקומות תפוסים: <strong>{occupiedSeats}</strong></span>
              <span>מקומות פנויים: <strong>{Math.max(totalSeats - occupiedSeats, 0)}</strong></span>
            </div>
            <span className="seating-summary__unassigned">אורחים ללא שולחן: <strong>{unassignedApprovedPeople}</strong></span>
          </div>
        </div>

        <div className="seating-actions">
          <button
            className="btn btn-outline-success btn-sm"
            disabled={tables.length === 0}
            onClick={() => exportSeatingToSpreadsheet({ tables: sortedTables, contacts: visibleContacts }, "סידורי הושבה")}
          >
            הורד אקסל
          </button>
          <button className="btn btn-outline-secondary btn-sm" disabled={tables.length === 0} onClick={handlePdfExport}>
            הדפס / שמור PDF
          </button>
        </div>

        <button
          type="button"
          className="btn btn-success seating-add-toggle"
          disabled={isReadOnly}
          aria-expanded={showAddTableForm}
          onClick={() => setShowAddTableForm((current) => !current)}
        >
          <span aria-hidden="true">＋</span>
          הוספת שולחן
        </button>

        {showAddTableForm && (
          <div className="seating-add-card">
            <div className="seating-add-card__header">
              <div className="seating-add-card__title">פרטי שולחן חדש</div>
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setShowAddTableForm(false)}>
                סגור
              </button>
            </div>
            <div className="seating-add-card__form">
              <label>
                שם שולחן
                <input
                  className="form-control form-control-sm"
                  disabled={isReadOnly}
                  placeholder="למשל: שולחן משפחה"
                  value={newTable.name}
                  onChange={(e) => setNewTable((prev) => ({ ...prev, name: e.target.value }))}
                />
              </label>
              <label>
                מספר שולחן
                <input
                  className="form-control form-control-sm"
                  disabled={isReadOnly}
                  placeholder="למשל: 1"
                  value={newTable.number}
                  onChange={(e) => setNewTable((prev) => ({ ...prev, number: e.target.value }))}
                />
              </label>
              <label>
                מספר מקומות
                <input
                  type="number"
                  min="1"
                  className="form-control form-control-sm"
                  disabled={isReadOnly}
                  value={newTable.capacity}
                  onChange={(e) => setNewTable((prev) => ({ ...prev, capacity: e.target.value }))}
                />
              </label>
              <button className="btn btn-success btn-sm seating-add-card__button" disabled={isReadOnly} onClick={() => handleAddTable()}>
                + הוסף שולחן
              </button>
            </div>
          </div>
        )}

        {sortedTables.length === 0 ? (
          <div className="seating-empty">אין שולחנות עדיין. הוסף שולחן כדי להתחיל לשבץ אורחים.</div>
        ) : (
          <div className="seating-grid">
            {sortedTables.map((table) => {
              const draft = getDraft(table);
              const assigned = getAssignedContacts(table.id);
              const tableOccupied = getOccupiedSeats(table.id);
              const capacity = parseInt(String(table.capacity), 10) || 0;
              const freeSeats = Math.max(capacity - tableOccupied, 0);
              const isGuestListExpanded = Boolean(expandedGuestTables[table.id]);
              return (
                <div className="seating-table-card" key={table.id}>
                  <div
                    className="seating-table-card__header"
                    role="button"
                    tabIndex={0}
                    aria-expanded={isGuestListExpanded}
                    onClick={() => toggleGuestTable(table.id)}
                    onKeyDown={(event) => {
                      if (event.target !== event.currentTarget || (event.key !== "Enter" && event.key !== " ")) return;
                      event.preventDefault();
                      toggleGuestTable(table.id);
                    }}
                  >
                    <div className="seating-table-card__number">שולחן {table.number}</div>
                    <div className="seating-table-card__name">{table.name}</div>
                    <div className="seating-table-card__availability">
                      <strong>{freeSeats}</strong> פנויים מתוך {capacity} מקומות
                    </div>
                    <button
                      type="button"
                      className="seating-table-card__edit-toggle"
                      disabled={isReadOnly}
                      onClick={(event) => {
                        event.stopPropagation();
                        if (editingTableId === table.id) {
                          handleCancelTableEdit(table.id);
                        } else {
                          setDeleteConfirmId(null);
                          setEditingTableId(table.id);
                        }
                      }}
                    >
                      <span aria-hidden="true">✎</span>
                      {editingTableId === table.id ? "סגירת עריכה" : "עריכת שולחן"}
                    </button>
                  </div>

                  {editingTableId === table.id && <div className="seating-table-card__editing-panel">
                  <div className="seating-table-card__edit">
                    <input
                      className="form-control form-control-sm"
                      disabled={isReadOnly}
                      value={draft.name}
                      onChange={(e) => handleTableEdit(table, "name", e.target.value)}
                      aria-label="שם שולחן"
                    />
                    <input
                      className="form-control form-control-sm"
                      disabled={isReadOnly}
                      value={draft.number}
                      onChange={(e) => handleTableEdit(table, "number", e.target.value)}
                      aria-label="מספר שולחן"
                    />
                    <input
                      type="number"
                      min="1"
                      className="form-control form-control-sm"
                      disabled={isReadOnly}
                      value={draft.capacity}
                      onChange={(e) => handleTableEdit(table, "capacity", e.target.value)}
                      aria-label="מספר מקומות"
                    />
                    <button className="btn btn-primary btn-sm" disabled={isReadOnly} onClick={() => handleSaveTable(table)}>
                      שמור
                    </button>
                    <button className="btn btn-outline-secondary btn-sm" onClick={() => handleCancelTableEdit(table.id)}>
                      ביטול
                    </button>
                  </div>

                  <div className="seating-chair-controls">
                    <button className="btn btn-outline-secondary btn-sm" disabled={isReadOnly} onClick={() => handleCapacityChange(table, 1)}>
                      + הוסף כיסא
                    </button>
                    <span>{capacity} מקומות</span>
                    <button className="btn btn-outline-secondary btn-sm" disabled={isReadOnly} onClick={() => handleCapacityChange(table, -1)}>
                      - הסר כיסא
                    </button>
                  </div>

                  <div className="seating-table-card__footer">
                    {deleteConfirmId === table.id ? (
                      <>
                        <button className="btn btn-danger btn-sm" disabled={isReadOnly} onClick={() => handleDeleteTable(table.id)}>
                          כן, מחק שולחן
                        </button>
                        <button className="btn btn-outline-secondary btn-sm" onClick={() => setDeleteConfirmId(null)}>
                          ביטול
                        </button>
                      </>
                    ) : (
                      <button className="btn btn-danger btn-sm" disabled={isReadOnly} onClick={() => setDeleteConfirmId(table.id)}>
                        מחק שולחן
                      </button>
                    )}
                  </div>
                  </div>}

                  <button
                    type="button"
                    className="seating-guest-list-toggle"
                    aria-expanded={isGuestListExpanded}
                    onClick={() => toggleGuestTable(table.id)}
                  >
                    <span
                      className={`seating-guest-list-toggle__arrow ${isGuestListExpanded ? "seating-guest-list-toggle__arrow--open" : ""}`}
                      aria-hidden="true"
                    >
                      ▼
                    </span>
                  </button>

                  <div
                    className={`seating-table-card__dropdown-content ${isGuestListExpanded ? "seating-table-card__dropdown-content--open" : ""}`}
                    aria-hidden={!isGuestListExpanded}
                    inert={isGuestListExpanded ? undefined : ""}
                  >
                    <div className="seating-table-card__dropdown-inner">
                    <div
                      className="seating-diagram"
                      role="button"
                      tabIndex={0}
                      aria-label={`סגירת תרשים שולחן ${table.number}: ${tableOccupied} מקומות תפוסים מתוך ${capacity}`}
                      onClick={() => toggleGuestTable(table.id)}
                      onKeyDown={(event) => {
                        if (event.key !== "Enter" && event.key !== " ") return;
                        event.preventDefault();
                        toggleGuestTable(table.id);
                      }}
                    >
                      <div className="seating-diagram__canvas">
                        <div className="seating-diagram__table">
                          <strong>{table.name}</strong>
                          <span>שולחן {table.number}</span>
                          <div className="seating-diagram__legend">
                            <span><i className="seating-diagram__legend-dot seating-diagram__legend-dot--occupied" /> תפוס ({tableOccupied})</span>
                            <span><i className="seating-diagram__legend-dot seating-diagram__legend-dot--free" /> פנוי ({freeSeats})</span>
                          </div>
                        </div>
                        {Array.from({ length: capacity }, (_, seatIndex) => {
                          const isOccupied = seatIndex < tableOccupied;
                          return (
                            <span
                              className={`seating-diagram__seat ${isOccupied ? "seating-diagram__seat--occupied" : "seating-diagram__seat--free"}`}
                              key={seatIndex}
                              style={getSeatPosition(seatIndex, capacity)}
                              title={`מושב ${seatIndex + 1} - ${isOccupied ? "תפוס" : "פנוי"}`}
                              aria-label={`מושב ${seatIndex + 1} - ${isOccupied ? "תפוס" : "פנוי"}`}
                            >
                              {seatIndex + 1}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                    <div className="seating-seat-list">
                      {assigned.length === 0 ? (
                        <div className="seating-seat-list__empty">אין אורחים בשולחן זה.</div>
                      ) : (
                        assigned.map(({ contact, idx }) => (
                          <div className="seating-party" key={`${contact.phoneNumber}-${idx}`}>
                            <div className="seating-party__header">
                              <span>{contact.name} ({getPartySize(contact)} מקומות)</span>
                              <div className="seating-party__actions">
                                {removeConfirmIdx === idx ? (
                                  <>
                                    <button className="btn btn-danger btn-sm" disabled={isReadOnly} onClick={() => handleRemoveParty(idx)}>כן</button>
                                    <button className="btn btn-outline-secondary btn-sm" onClick={() => setRemoveConfirmIdx(null)}>לא</button>
                                  </>
                                ) : (
                                  <button className="btn btn-outline-danger btn-sm" disabled={isReadOnly} onClick={() => setRemoveConfirmIdx(idx)}>
                                    הסר מהשולחן
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                    </div>
                  </div>

                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default SeatingArrangements;
